import React, { useState } from "react";
import Papa from "papaparse";
import PropTypes from "prop-types";
import { Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Typography, Chip, Divider, Box, Alert, Grid, LinearProgress, useTheme, Table, TableCell, TableBody, TableRow } from "@mui/material";
import Button from '../../components/Button';
import { FolderSimple, MicrosoftExcelLogo, UploadSimple } from "@phosphor-icons/react";
import { imageFileFilter } from "../../utils";
import { toast as toastFn } from 'react-toastify';
import { uploadImage } from "../../store/course/courseBaseApi";
import { makeStyles } from 'tss-react/mui';

function LinearProgressWithLabel(props) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            <Box sx={{ width: '80%', mr: 1, }}>
                <LinearProgress variant="determinate" {...props} sx={{ height: 10, borderRadius: 5 }} />
            </Box>
            <Box sx={{ minWidth: 35 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {`${Math.round(props.value)}%`}
                </Typography>
            </Box>
        </Box>
    );
}

LinearProgressWithLabel.propTypes = {
    /**
     * The value of the progress indicator for the determinate and buffer variants.
     * Value between 0 and 100.
     */
    value: PropTypes.number.isRequired,
};

const useStyles = makeStyles()((theme) => ({
    dialogOverlay: {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(255, 255, 255, 0.9)", // Semi-transparent background
        zIndex: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: theme.spacing(1),
    },
    dragDropArea: {
        border: "2px dashed #ccc",
        borderRadius: "8px",
        padding: "20px",
        textAlign: "center",
        cursor: "pointer",
        backgroundColor: "#f9f9f9",
    },
    dialogTitle: {
        margin: 0,
        fontWeight: 700,
        lineHeight: '1.5',
    },
    dialogActions: {
        justifyContent: "space-between",
        padding: theme.spacing(2.5),
    },
    tableCell: {
        fontSize: '10px',
        border: "1px solid #ddd",
        padding: '2px',
        color: 'grey'
    },
    table: {
        border: "1px solid #ddd",
        minWidth: 300
    }

}));


export const CsvEditor = ({ onUpload, chapterId }) => {
    const { classes } = useStyles();
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const [data, setData] = useState([]);
    const [csvFileName, setCsvFileName] = useState("");
    const [folderName, setFolderName] = useState("");
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);


    const showToast = (msg, type) => {
        if (!type) {
            return toastFn(msg);
        }

        toastFn[type](msg);
    }

    // Open/Close Dialog
    const handleClickOpen = () => setOpen(true);
    const handleClose = () => {
        setCsvFileName("");
        setFolderName("");
        setData([]);
        setOpen(false);
    }

    // Handle CSV Upload
    const handleFileUpload = (file) => {
        if (!file) return;

        setCsvFileName(file.name); // Store CSV file name

        Papa.parse(file, {
            complete: (result) => {
                const parsedData = result.data
                    .filter(row => row.length >= 2 && row[0].trim() !== "" && row[1].trim() !== "")
                    .map(([sentence, imagePath]) => {
                        // Ensure the sentence ends with a period if it doesn't
                        if (!sentence.trim().endsWith('.')) {
                            sentence = sentence.trim() + '.'; // Add period if missing
                        }
                        return {
                            sentence,
                            imagePath,
                            file: null,
                            uploadedUrl: null
                        };
                    });

                setData(parsedData);
            },
            header: false,
            skipEmptyLines: true
        });
    };

    // Handle Folder Upload
    const handleFolderUpload = (files) => {
        if (!files.length) return;

        const folderPath = files[0].webkitRelativePath.split("/")[0]; // Extract folder name
        setFolderName(folderPath); // Store folder name for display

        const newFilesMap = {};

        for (let file of files) {
            const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, ""); // Remove extension
            newFilesMap[file.name] = file;  // Store file with extension
            newFilesMap[fileNameWithoutExt] = file;  // Store file without extension
            newFilesMap[file.webkitRelativePath] = file; // Store file by path
        }

        const updatedData = data.map((item) => {
            const imageNameWithoutExt = item.imagePath.replace(/\.[^/.]+$/, ""); // Remove extension
            return {
                ...item,
                file: newFilesMap[item.imagePath] || newFilesMap[imageNameWithoutExt] || newFilesMap[`${folderPath}/${item.imagePath}`] || null,
            };
        });

        setData(updatedData);
    };

    const removeCsvFile = () => {
        setCsvFileName("");
        setData([]);
    };

    const removeFolder = () => {
        setFolderName("");
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDropCSV = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const files = event.dataTransfer.files;
        if (files.length > 0) {
            handleFileUpload(files[0]);
        }
    };

    const handleDropFolder = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const files = event.dataTransfer.files;
        if (files.length > 0) {
            handleFolderUpload(files);
        }
    };

    // Upload All Images & Generate Formatted Text
    const handleBulkUpload = async () => {
        setLoading(true);
        setUploadProgress(0);
        const updatedData = [...data];

        for (let i = 0; i < updatedData.length; i++) {
            const file = updatedData[i].file;
            if (!file) {
                showToast(`No matching image found for: ${updatedData[i].imagePath}`, 'error');
                setLoading(false);
                return;
            }

            const fileSizeInMB = (file.size / 1024 / 1024)
            if (!imageFileFilter(file.name)) {
                showToast(`Invalid valid image file - ${file.name}`, 'error');
                setLoading(false);
                return;
            } else if (fileSizeInMB > 5) {
                showToast(`Please upload file with in the 5 MB - ${file.name}`, 'error');
                setLoading(false);
                return;
            }

            const formData = new FormData();
            formData.append('file', file);

            try {
                const resp = await uploadImage({ id: chapterId, formData, field: 'imageUrl' });
                if (resp.data) {
                    updatedData[i].uploadedUrl = resp.data;
                } else {
                    showToast(`Failed to upload file - ${file.name}`, 'error');
                }
            } catch (e) {
                showToast(e.error || 'An error occurred while uploading', 'error');
                continue;
            }
            setUploadProgress(Math.round(((i + 1) / updatedData.length) * 100));
            await new Promise(resolve => setTimeout(resolve, 100));
        }

        setData(updatedData);

        //  Generate final formatted text
        const formattedString = updatedData
            .map(item => `[${item.sentence}]{${item.uploadedUrl}}`)
            .join("\n");

        onUpload(formattedString);
        showToast('Content Uploaded Successfully', 'success');

        // Reset CSV and folder after success/failure
        setCsvFileName("");
        setFolderName("");
        setData([]);

        setLoading(false);
        // handleClose();
    };

    return (
        <div style={{ textAlign: "left" }}>
            {/* Bulk Upload Button */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Button
                    variant="outlined"
                    size="large"
                    color="primary"
                    startIcon={<UploadSimple size={20} />}
                    onClick={handleClickOpen}
                    placeholder="Upload Content"
                />
                <Typography sx={{ color: 'grey', fontSize: '0.8em', mt: 1 }}>
                    "Click <span style={{ color: '#000', fontWeight: '600' }}>'Upload Content'</span> to easily upload your sentences and images."
                </Typography>
            </Box>
            {/* Dialog Popup */}
            <Dialog open={open} fullWidth maxWidth="sm">
                {loading && (
                    <Box className={classes.dialogOverlay}>
                        <CircularProgress />
                        <Typography variant="body1">Uploading... Please wait</Typography>
                        <LinearProgressWithLabel value={uploadProgress} />
                    </Box>
                )}
                <DialogTitle  >
                    <Typography className={classes.dialogTitle} align="center" variant="h6" color="primary.blue">
                        File Upload
                    </Typography>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Alert severity="info" sx={{ fontSize: '12px' }}> <span style={{ color: '#000', fontWeight: 600 }}>Note :</span> Please upload a CSV file and select the folder containing the images. The filenames in the CSV must match the image files in the selected folder.<br />
                                <span style={{ color: '#000', fontWeight: 500, }}>CSV format :</span>
                                <Table className={classes.table}>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell className={classes.tableCell} >The cat is on the mat.
                                            </TableCell>
                                            <TableCell className={classes.tableCell}>cat.jpg</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell className={classes.tableCell}>The sun is shining.</TableCell>
                                            <TableCell className={classes.tableCell}>sun.jpg</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                                <span style={{ color: 'grey', fontSize: '0.8em', fontWeight: 500 }}>
                                    (<span style={{ color: '#000' }}>.jpg, .jpeg, .png, .gif</span> files only, up to <span style={{ color: '#000' }}>5MB</span>)
                                </span>
                            </Alert>
                        </Grid>
                        {/* CSV Upload Drop Area */}
                        <Grid item xs={12} sm={6}>
                            <Box
                                onDragOver={handleDragOver}
                                onDrop={handleDropCSV}
                                className={classes.dragDropArea}
                            >
                                <input type="file" accept=".csv" onChange={(e) => handleFileUpload(e.target.files[0])} style={{ display: "none" }} id="csv-upload" />
                                <label htmlFor="csv-upload" style={{ display: "block", cursor: 'pointer' }}>
                                    <MicrosoftExcelLogo size={32} color="green" />
                                    <Typography variant="body1">Drag & Drop CSV File or Click to Upload</Typography>
                                </label>
                                {csvFileName && <Chip label={csvFileName} onDelete={removeCsvFile} sx={{ mt: 2 }} />}
                            </Box>
                        </Grid>
                        {/* Folder Upload Drop Area */}
                        <Grid item xs={12} sm={6}>
                            <Box
                                onDragOver={handleDragOver}
                                onDrop={handleDropFolder}
                                className={classes.dragDropArea}
                            >
                                <input type="file" webkitdirectory="" directory="" multiple onChange={(e) => handleFolderUpload(e.target.files)} style={{ display: "none" }} id="folder-upload" />
                                <label htmlFor="folder-upload" style={{ display: "block", cursor: 'pointer' }}>
                                    <FolderSimple size={32} color="orange" />
                                    <Typography variant="body1">Select a Folder Containing Images </Typography>
                                </label>
                                {folderName && <Chip label={`./${folderName}`} onDelete={removeFolder} sx={{ mt: 2 }} />}
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions className={classes.dialogActions} >
                    <Button size='large' onClick={handleClose} variant='outlined' placeholder={'Cancel'} sx={{ mr: 2 }} />
                    <Button size='large' onClick={handleBulkUpload} color="primary" variant="contained" placeholder="Upload" disabled={(!csvFileName || !folderName)} />
                </DialogActions>
            </Dialog>
        </div >
    );
};
